import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Calendar, Filter } from 'lucide-react';
import axios from 'axios';

const HOLIDAY_TYPES = [
  { value: 'national', label: 'National Holiday', color: 'bg-red-100 text-red-700' },
  { value: 'religious', label: 'Religious Holiday', color: 'bg-purple-100 text-purple-700' },
  { value: 'regional', label: 'Regional Holiday', color: 'bg-blue-100 text-blue-700' },
  { value: 'institutional', label: 'Institutional', color: 'bg-green-100 text-green-700' },
  { value: 'vacation', label: 'Vacation', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'other', label: 'Other', color: 'bg-gray-100 text-gray-700' }
];

const Holidays = () => {
  const [holidays, setHolidays] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedType, setSelectedType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    holiday_type: 'national',
    description: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  // Generate year options (5 years back to 2 years ahead)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear - 5; y <= currentYear + 2; y++) {
    yearOptions.push(y.toString());
  }

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/master-settings/holidays`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        date: formData.date,
        holiday_type: formData.holiday_type,
        description: formData.description
      };

      if (editingHoliday) {
        await axios.put(`${API_URL}/api/master-settings/holidays/${editingHoliday.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/master-settings/holidays`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchHolidays();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving holiday:', error);
      alert('Failed to save holiday. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this holiday?')) {
      setLoading(true);
      try {
        await axios.delete(`${API_URL}/api/master-settings/holidays/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchHolidays();
      } catch (error) {
        console.error('Error deleting holiday:', error);
        alert('Failed to delete holiday. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      name: holiday.name,
      date: holiday.date,
      holiday_type: holiday.holiday_type || 'national',
      description: holiday.description || ''
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingHoliday(null);
    setFormData({ name: '', date: '', holiday_type: 'national', description: '' });
  };

  // Filter holidays
  const filteredHolidays = holidays.filter(holiday => {
    const matchesSearch = holiday.name.toLowerCase().includes(searchTerm.toLowerCase());
    const holidayYear = holiday.date ? holiday.date.substring(0, 4) : '';
    const matchesYear = selectedYear === '' || holidayYear === selectedYear;
    const matchesType = selectedType === '' || holiday.holiday_type === selectedType;
    return matchesSearch && matchesYear && matchesType;
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  // Group by month
  const groupedByMonth = filteredHolidays.reduce((acc, holiday) => {
    const date = new Date(holiday.date);
    const monthKey = date.toLocaleString('default', { month: 'long', year: 'numeric' });
    if (!acc[monthKey]) acc[monthKey] = [];
    acc[monthKey].push(holiday);
    return acc;
  }, {});

  const getHolidayTypeColor = (type) => {
    const found = HOLIDAY_TYPES.find(t => t.value === type);
    return found ? found.color : 'bg-gray-100 text-gray-700';
  };

  const getHolidayTypeLabel = (type) => {
    const found = HOLIDAY_TYPES.find(t => t.value === type);
    return found ? found.label : 'Other';
  };

  // Stats
  const stats = HOLIDAY_TYPES.map(type => ({
    ...type,
    count: filteredHolidays.filter(h => h.holiday_type === type.value).length
  }));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Holidays Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage holidays and vacation calendar</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          Add Holiday
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search holidays..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">All Years</option>
              {yearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
          <div>
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg"
            >
              <option value="">All Types</option>
              {HOLIDAY_TYPES.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </div>
          <div className="text-right">
            <span className="text-sm text-gray-600">
              {filteredHolidays.length} holidays found
            </span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {stats.map(stat => (
          <div key={stat.value} className={`p-3 rounded-lg border ${stat.color.replace('text-', 'border-').replace('-700', '-200')}`}>
            <p className={`text-xs ${stat.color.split(' ')[1]}`}>{stat.label}</p>
            <p className={`text-xl font-bold ${stat.color.split(' ')[1]}`}>{stat.count}</p>
          </div>
        ))}
      </div>

      {/* Holidays List by Month */}
      <div className="space-y-4">
        {Object.keys(groupedByMonth).length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center border">
            <Calendar className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-2 text-gray-500">No holidays found for the selected filters</p>
          </div>
        ) : (
          Object.entries(groupedByMonth).map(([month, monthHolidays]) => (
            <div key={month} className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="bg-gray-50 px-4 py-2 border-b">
                <h3 className="font-semibold text-gray-700">{month}</h3>
              </div>
              <div className="divide-y">
                {monthHolidays.map(holiday => (
                  <div key={holiday.id} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="text-center bg-blue-50 rounded-lg p-2 min-w-[60px]">
                        <p className="text-2xl font-bold text-blue-600">
                          {new Date(holiday.date).getDate()}
                        </p>
                        <p className="text-xs text-blue-500">
                          {new Date(holiday.date).toLocaleString('default', { weekday: 'short' })}
                        </p>
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{holiday.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getHolidayTypeColor(holiday.holiday_type)}`}>
                            {getHolidayTypeLabel(holiday.holiday_type)}
                          </span>
                          {holiday.description && (
                            <span className="text-xs text-gray-500">{holiday.description}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => handleEdit(holiday)} className="p-2 text-blue-600 hover:bg-blue-50 rounded">
                        <Edit className="h-4 w-4" />
                      </button>
                      <button onClick={() => handleDelete(holiday.id)} className="p-2 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Name *</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-2 border rounded-lg"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Republic Day"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    className="w-full px-4 py-2 border rounded-lg"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                  <select
                    required
                    className="w-full px-4 py-2 border rounded-lg"
                    value={formData.holiday_type}
                    onChange={(e) => setFormData({ ...formData, holiday_type: e.target.value })}
                  >
                    {HOLIDAY_TYPES.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  className="w-full px-4 py-2 border rounded-lg"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={handleCloseModal} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400">
                  {loading ? 'Saving...' : (editingHoliday ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Holidays;
